import { useLingui } from '@lingui/react/macro'
import { LucideIcon } from 'components/LucideIcon'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import React from 'react'

import {
  EmbeddedSubDropdownActionLabel,
  EmbeddedSubDropdownContent,
  EmbeddedSubDropdownItemLabel,
  EmbeddedSubDropdownLabel,
  EmbeddedSubDropdownRow,
  EmbeddedSubDropdownSearch,
  EmbeddedSubDropdownTrigger,
} from './siteSelectorsSidebar.styles'

export type EmbeddedSelectorSectionProps<T> = {
  label: string
  selectedLabel: string
  selectedEditLabel?: string
  onEditSelected?: (event: React.MouseEvent<HTMLButtonElement>) => void
  items: T[]
  getItemId: (item: T) => string
  getItemName: (item: T) => string
  isOpen: boolean
  onToggleOpen: () => void
  searchValue: string
  searchPlaceholder: string
  onSearchChange: (value: string) => void
  canEdit: boolean
  canAdd: boolean
  addLabel: string
  triggerDisabled?: boolean
  onAdd: (event: React.MouseEvent<HTMLButtonElement>) => void
  onEdit: (event: React.MouseEvent<HTMLButtonElement>, item: T) => void
  onSelect: (item: T) => void
}

export function EmbeddedSelectorSection<T>({
  label,
  selectedLabel,
  selectedEditLabel,
  onEditSelected,
  items,
  getItemId,
  getItemName,
  isOpen,
  onToggleOpen,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  canEdit,
  canAdd,
  addLabel,
  triggerDisabled = false,
  onAdd,
  onEdit,
  onSelect,
}: EmbeddedSelectorSectionProps<T>) {
  const { t } = useLingui()
  return (
    <Box display='flex' direction='column' gap='sm' padding='md'>
      <Box display='flex' align='center' justify='between' gap='sm'>
        <Text variant='label' weight='semibold'>
          {label}
        </Text>
        <Box display='inlineFlex' shrink={false} align='center' gap='xs'>
          {canEdit && selectedEditLabel && onEditSelected ? (
            <Button
              type='button'
              variant='outline'
              size='sm'
              aria-label={t`Edit ${selectedEditLabel}`}
              onClick={(event) => void onEditSelected(event)}>
              {t`Edit`}
            </Button>
          ) : null}
        </Box>
      </Box>
      <EmbeddedSubDropdownTrigger
        type='button'
        disabled={triggerDisabled}
        onClick={onToggleOpen}>
        <EmbeddedSubDropdownLabel>{selectedLabel}</EmbeddedSubDropdownLabel>
        <LucideIcon name='ChevronDown' size={16} />
      </EmbeddedSubDropdownTrigger>
      {isOpen ? (
        <EmbeddedSubDropdownContent>
          <EmbeddedSubDropdownSearch
            value={searchValue}
            placeholder={searchPlaceholder}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          {canAdd ? (
            <Button
              type='button'
              variant='ghost'
              size='lg'
              width='full'
              onClick={(event) => void onAdd(event)}>
              <EmbeddedSubDropdownActionLabel>
                {addLabel}
              </EmbeddedSubDropdownActionLabel>
              <LucideIcon name='Plus' size={16} />
            </Button>
          ) : null}
          {items.map((item) => {
            const name = getItemName(item)
            return (
              <EmbeddedSubDropdownRow key={getItemId(item)}>
                <Button
                  type='button'
                  variant='ghost'
                  size='lg'
                  width='full'
                  onClick={() => onSelect(item)}>
                  <EmbeddedSubDropdownItemLabel>
                    {name}
                  </EmbeddedSubDropdownItemLabel>
                </Button>
                {canEdit ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon-lg'
                    aria-label={t`Edit ${name}`}
                    onClick={(event) => void onEdit(event, item)}>
                    <LucideIcon name='Pencil' size={16} />
                  </Button>
                ) : null}
              </EmbeddedSubDropdownRow>
            )
          })}
        </EmbeddedSubDropdownContent>
      ) : null}
    </Box>
  )
}
